import React, { useEffect, useState } from "react";
import { PageHeader, Card, Badge, Button, Spinner, Input, Select, EmptyState } from "@/components/ui";
import { endpoints } from "@/lib/api";
import { Kanban, Plus, X, Trash2 } from "lucide-react";

const METHODOLOGIES = ["Agile", "Waterfall", "PMBOK", "Prince2", "Lean", "Stage-Gate"];
const STATUSES = ["planning", "in_progress", "on_hold", "completed"];
const TYPES = ["pmi", "restructuring", "integration", "exit_readiness"];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", project_type: "pmi", methodology: "Agile", status: "planning", progress: 0, owner: "" });

  const load = async () => {
    setLoading(true);
    try { setProjects((await endpoints.projects()).data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await endpoints.createProject({ ...form, progress: Number(form.progress) });
    setShowAdd(false);
    setForm({ name: "", project_type: "pmi", methodology: "Agile", status: "planning", progress: 0, owner: "" });
    load();
  };

  const updateProgress = async (p, delta) => {
    const next = Math.max(0, Math.min(100, p.progress + delta));
    await endpoints.updateProject(p.id, { progress: next, status: next === 100 ? "completed" : "in_progress" });
    load();
  };

  const del = async (id) => {
    if (!confirm("Delete this project?")) return;
    await endpoints.deleteProject(id);
    load();
  };

  return (
    <div className="p-4 md:p-10 max-w-[1500px] mx-auto">
      <PageHeader
        eyebrow="CLARA Project"
        title="Execution & Delivery"
        description="Post-merger integration, restructuring and exit-readiness programs. Agile · Waterfall · PMBOK · Lean · Stage-Gate."
        testid="projects-page"
        actions={<Button onClick={() => setShowAdd(true)} data-testid="add-project-btn"><Plus size={14} /> New Program</Button>}
      />

      {loading ? <Spinner /> :
       !projects.length ? <EmptyState icon={Kanban} title="No programs yet" description="Create your first execution program." action={<Button onClick={() => setShowAdd(true)}>New Program</Button>} /> :
      (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <Card key={p.id} data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 pr-3">
                  <div className="font-display text-lg leading-tight mb-1">{p.name}</div>
                  <div className="flex flex-wrap gap-1">
                    <Badge tone="gold">{p.methodology}</Badge>
                    <Badge tone="navy">{p.project_type.replace("_", " ")}</Badge>
                    <Badge tone={p.status === "completed" ? "green" : p.status === "on_hold" ? "yellow" : "neutral"}>{p.status.replace("_", " ")}</Badge>
                  </div>
                </div>
                <button onClick={() => del(p.id)} className="text-white/30 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[11px] text-white/50">Progress</div>
                  <div className="text-[12px] font-mono text-gold">{p.progress}%</div>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${p.progress}%`, background: "linear-gradient(90deg, #C5A059, #D4AF37)" }} />
                </div>
              </div>
              <div className="text-[11px] text-white/40 mb-3">Owner: {p.owner || "—"}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => updateProgress(p, -10)}>−10%</Button>
                <Button size="sm" variant="outline" onClick={() => updateProgress(p, 10)}>+10%</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShowAdd(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="clara-card w-full max-w-md p-6" data-testid="add-project-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl">New Program</h2>
              <button type="button" onClick={() => setShowAdd(false)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="overline mb-1 block">Name</label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="overline mb-1 block">Type</label>
                  <Select value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })}>
                    {TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                  </Select>
                </div>
                <div>
                  <label className="overline mb-1 block">Method</label>
                  <Select value={form.methodology} onChange={(e) => setForm({ ...form, methodology: e.target.value })}>
                    {METHODOLOGIES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </Select>
                </div>
              </div>
              <div>
                <label className="overline mb-1 block">Owner</label>
                <Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} />
              </div>
            </div>
            <Button type="submit" className="w-full mt-6 justify-center">Create Program</Button>
          </form>
        </div>
      )}
    </div>
  );
}
